/**
 * 👻 **The plot shadow — where the storehouse would stand, before you place it.**
 *
 * **D109.** 🎯 **haugur, 2026-09-03:** *"place a grey shadow on plot, turns red
 * if plot is not empty."* — and, at the end of the day, *"les finish the grey
 * or red shadow for búr build."*
 *
 * ## What it is
 *
 * A flat, translucent slab the size of the footprint, lying on the ground where
 * the building would go, drawn only while a `bur` is in your hand and you are
 * aiming at something. **Grey** when the plot is clear and flat, **red** when it
 * is not — the same two tests the server makes when you place
 * (`applyEdit`: every cell air, every column on something solid), asked of the
 * client's own loaded blocks through {@link plotVerdict}, so the shadow and the
 * refusal cannot disagree about what a plot is.
 *
 * ## 🔴 The material
 *
 * **The cracks' exact `MeshBasicMaterial`** — transparent, no depth test, no
 * map — which haugur has watched working for weeks under WebGPU
 * (`main.ts`, the break box before it). Nothing here compiles for the first
 * time. ⚠️ The colour is changed on the one material, never a second material.
 *
 * ## Where it sits
 *
 * In **render-origin space**, like the highlight: world coordinates are ISN93
 * metres and float32 cannot hold them. The caller passes the door cell and the
 * facing; the centre comes from `burCentre`, the size from `BUR_FOOTPRINT`,
 * turned for the facing.
 */

import { BoxGeometry, DoubleSide, Mesh, MeshBasicMaterial, type Vector3 } from 'three';
import {
  BUR_FOOTPRINT,
  burCentre,
  burFrame,
  plotVerdict,
  rumCentre,
  rumPlotVerdict,
  pitPlotVerdict,
  pitClearable,
  type BurFacing,
} from '@lofi/shared';

/** The two answers, and their colours — the tolg grey and the hverleir red. */
const FREE = 0xbfb29c;
const BLOCKED = 0xc8503c;

export class BurShadow {
  readonly mesh: Mesh;
  private readonly material: MeshBasicMaterial;

  constructor() {
    this.material = new MeshBasicMaterial({
      color: FREE,
      transparent: true,
      opacity: 0.35,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
    });
    /* A slab 12 cm thick: thin enough to read as a mark on the ground, thick
       enough not to z-fight with it even without a depth test. */
    this.mesh = new Mesh(new BoxGeometry(1, 0.12, 1), this.material);
    this.mesh.visible = false;
    /* Drawn after the world, like the cracks and for the same reason. */
    this.mesh.renderOrder = 998;
  }

  /** Hide it — nothing in hand, or nothing under the crosshair. */
  hide(): void {
    this.mesh.visible = false;
  }

  /**
   * Show it for a building whose door would be at `door`, facing `facing`.
   *
   * @param blockAt the client's own block lookup, world coordinates
   * @param isSolid whether a block id can be stood on
   * @param renderOrigin the origin every mesh is placed relative to
   */
  show(
    door: { x: number; y: number; z: number },
    facing: BurFacing,
    blockAt: (x: number, y: number, z: number) => number,
    isSolid: (id: number) => boolean,
    renderOrigin: Vector3,
  ): void {
    const verdict = plotVerdict(door, facing, blockAt, isSolid);
    this.material.color.setHex(verdict === 'free' ? FREE : BLOCKED);

    const { into } = burFrame(facing);
    const c = burCentre(door, facing);
    /* Along x when the building runs east–west, along z when it runs north–south. */
    const alongX = into.x !== 0;
    this.mesh.scale.set(
      alongX ? BUR_FOOTPRINT.length : BUR_FOOTPRINT.width,
      1,
      alongX ? BUR_FOOTPRINT.width : BUR_FOOTPRINT.length,
    );
    this.mesh.position.set(
      c.x + 0.5 - renderOrigin.x,
      c.y + 0.06 - renderOrigin.y,
      c.z + 0.5 - renderOrigin.z,
    );
    this.mesh.visible = true;
  }

  /**
   * 🛏️ **The same shadow, bed-sized** — D133. Two cells, the head where you
   * aim and the foot away from you, grey if free and red if not — by
   * `rumPlotVerdict`, the rule the server refuses with.
   */
  /** 🏠 D135 — the pit house's 7 × 6 plot, by `pitPlotVerdict` (the server's own check). */
  showPit(
    door: { x: number; y: number; z: number },
    facing: BurFacing,
    blockAt: (x: number, y: number, z: number) => number,
    isSolid: (id: number) => boolean,
    isFluid: (id: number) => boolean,
    renderOrigin: Vector3,
  ): void {
    /* 🧹 What placing will clear, or dig, is not "in the way" — the server's own rule. */
    const verdict = pitPlotVerdict(
      door,
      facing,
      blockAt,
      isSolid,
      isFluid,
      pitClearable,
      pitClearable,
    );
    this.material.color.setHex(verdict === 'free' ? FREE : BLOCKED);
    const { into } = burFrame(facing);
    /* The middle of the 7 × 6: 2.5 cells in from the door, centred across it. */
    const cx = door.x + into.x * 2.5;
    const cz = door.z + into.z * 2.5;
    const alongX = into.x !== 0;
    this.mesh.scale.set(alongX ? 6 : 7, 1, alongX ? 7 : 6);
    this.mesh.position.set(
      cx + 0.5 - renderOrigin.x,
      door.y + 0.06 - renderOrigin.y,
      cz + 0.5 - renderOrigin.z,
    );
    this.mesh.visible = true;
  }

  showBed(
    head: { x: number; y: number; z: number },
    facing: BurFacing,
    blockAt: (x: number, y: number, z: number) => number,
    isSolid: (id: number) => boolean,
    renderOrigin: Vector3,
  ): void {
    const verdict = rumPlotVerdict(head, facing, blockAt, isSolid);
    this.material.color.setHex(verdict === 'free' ? FREE : BLOCKED);
    const { into } = burFrame(facing);
    const c = rumCentre(head, facing);
    const alongX = into.x !== 0;
    this.mesh.scale.set(alongX ? 2 : 1, 1, alongX ? 1 : 2);
    this.mesh.position.set(
      c.x + 0.5 - renderOrigin.x,
      c.y + 0.06 - renderOrigin.y,
      c.z + 0.5 - renderOrigin.z,
    );
    this.mesh.visible = true;
  }
}
